import { useState, useRef } from 'react';
import InfoIcon from './InfoIcon.jsx';
import './CoursePicker.css';

export default function CoursePicker({
  mode,
  max,
  options,
  selected,
  onChange,
  dimmedIds = [],
}) {
  const [shakeKey, setShakeKey] = useState(0);
  const [showMaxMsg, setShowMaxMsg] = useState(false);
  const maxMsgTimer = useRef(null);

  const isSelected = (id) => {
    if (mode === 'single') return selected === id;
    return Array.isArray(selected) && selected.includes(id);
  };

  function handleSelect(id) {
    if (mode === 'single') {
      if (!isSelected(id)) onChange(id);
    } else {
      if (isSelected(id)) {
        onChange((selected || []).filter(x => x !== id));
      } else {
        if ((selected || []).length >= max) {
          setShakeKey(k => k + 1);
          setShowMaxMsg(true);
          if (maxMsgTimer.current) clearTimeout(maxMsgTimer.current);
          maxMsgTimer.current = setTimeout(() => setShowMaxMsg(false), 3000);
          return;
        }
        onChange([...(selected || []), id]);
      }
    }
  }

  const atMax = mode === 'multi' && (selected || []).length >= max;
  const selectedCount = mode === 'multi' ? (selected || []).length : 0;

  return (
    <div className="course-picker">
      {mode === 'multi' && max != null && (
        <p
          key={shakeKey}
          className={`course-picker-count${shakeKey > 0 ? ' course-picker-count--shake' : ''}${showMaxMsg ? ' course-picker-count--max' : ''}`}
        >
          {showMaxMsg
            ? `Max ${max} — deselect one to add another.`
            : `${selectedCount} of ${max} selected`}
        </p>
      )}

      <div
        role={mode === 'single' ? 'radiogroup' : undefined}
        className="course-picker-options"
      >
        {options.map(course => {
          const sel = isSelected(course.id);
          const disabled = mode === 'multi' && atMax && !sel;
          const dimmed = dimmedIds.includes(course.id);

          return (
            <button
              key={course.id}
              type="button"
              role={mode === 'single' ? 'radio' : 'checkbox'}
              aria-checked={sel}
              disabled={disabled}
              className={[
                'course-picker-card',
                sel     ? 'course-picker-card--selected' : '',
                disabled ? 'course-picker-card--disabled' : '',
                dimmed  ? 'course-picker-card--dimmed' : '',
                course.verificationNeeded ? 'course-picker-card--caution' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => handleSelect(course.id)}
            >
              <span className="course-picker-indicator" aria-hidden="true">
                {mode === 'single' ? (
                  <span className={`cp-radio${sel ? ' cp-radio--checked' : ''}`} />
                ) : (
                  <span className={`cp-checkbox${sel ? ' cp-checkbox--checked' : ''}`}>
                    {sel && <span className="cp-check-mark">✓</span>}
                  </span>
                )}
              </span>

              <span className="course-picker-content">
                <span className="course-picker-name-row">
                  <span className="course-picker-name">{course.name}</span>
                  {course.code && (
                    <span className="course-picker-code">{course.code}</span>
                  )}
                  {course.source && <InfoIcon source={course.source} />}
                </span>

                {course.description && (
                  <span className="course-picker-desc">{course.description}</span>
                )}

                {course.note && (
                  <span className="course-picker-note">{course.note}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
